import {
  DeltakerutsendingerDeltakerResponseDto,
  DeltakerutsendingerResponseDto,
} from 'services/deltakerutsendinger/dto';
import {
  HenvendelseOpplistingIntervallDto,
  HenvisningssvarOpplistingIntervallDto,
} from 'services/henvendelser/dto';
import { HenvisningOpplistingIntervallDto } from 'services/henvisninger/dto';
import { IfobtPrøvesvarOpplistingIntervallDto } from 'services/iFobt/dto';
import { InklusjonOpplistingIntervallDto } from 'services/inklusjon/dto';
import { OpplistingTyperEnum } from 'services/opplisting/dto';
import {
  FetchResponseError,
  addMessageToExsistingFetchError,
} from 'utils/errors/customError';
import { buildPath, httpGet } from 'utils/http';

import { IntervallSearchDto } from './dto';
import {
  GetDeltaker,
  GetSenter,
  GetUtsendingPerDeltaker,
  GetUtsendingPerIntervall,
  HenvisningOpplistingReturnDto,
  HenvisningssvarOpplistingReturnDto,
  IfobtPrøvesvarOpplistingReturnDto,
  InklusjonMedDatoReturnDto,
  KoloskopirapportOpplistingIntervallDto,
  KoloskopirapportOpplistingReturnDto,
  SentraladminService,
} from './interface';

const getDeltaker: GetDeltaker = async (params, signal?) => {
  const urlSearchParams = new URLSearchParams(params);
  const { opplistingstype } = Object.fromEntries(urlSearchParams.entries());
  const type = opplistingstype ? Number(opplistingstype) : undefined;

  try {
    switch (type) {
      case OpplistingTyperEnum.Inklusjon:
        return await httpGet<InklusjonMedDatoReturnDto>(
          `${buildPath(`inklusjoner/intervall`)}?${params}`,
          signal
        );
      case OpplistingTyperEnum.Henvisning:
        return await httpGet<HenvisningOpplistingReturnDto>(
          `${buildPath(`henvisninger/intervall`)}?${params}`,
          signal
        );
      case OpplistingTyperEnum.Prøvesvar:
        return await httpGet<IfobtPrøvesvarOpplistingReturnDto>(
          `${buildPath(`ifobt/provesvar/intervall`)}?${params}`,
          signal
        );
      case OpplistingTyperEnum.Koloskopirapport:
        return await httpGet<KoloskopirapportOpplistingReturnDto>(
          `${buildPath(`koloskopirapporter/intervall`)}?${params}`,
          signal
        );
      case OpplistingTyperEnum.Henvendelse:
        return await httpGet<HenvisningOpplistingReturnDto>(
          `${buildPath(`henvendelser/intervall`)}?${params}`,
          signal
        );
      case OpplistingTyperEnum.Henvisningssvar:
        return await httpGet<HenvisningssvarOpplistingReturnDto>(
          `${buildPath(`henvisninger/svar/intervall`)}?${params}`,
          signal
        );
      default:
        return new Promise((resolve) => resolve([]));
    }
  } catch (error) {
    throw addMessageToExsistingFetchError(
      error as FetchResponseError,
      `Det skjedde en feil ved søk på deltaker: ${
        (error as FetchResponseError).message
      }`
    );
  }
};

const getSenter: GetSenter = async (params, signal?) => {
  const urlSearchParams = new URLSearchParams(params);
  const { opplistingstype, intervalType } = Object.fromEntries(
    urlSearchParams.entries()
  );
  const type = opplistingstype ? Number(opplistingstype) : undefined;
  try {
    if (intervalType) {
      switch (type) {
        case OpplistingTyperEnum.Inklusjon:
          return await httpGet<
            IntervallSearchDto<InklusjonOpplistingIntervallDto>
          >(
            `${buildPath(
              `inklusjoner/intervall/${intervalType.toLowerCase()}`
            )}?${params}`,
            signal
          );
        case OpplistingTyperEnum.Henvisning:
          return await httpGet<
            IntervallSearchDto<HenvisningOpplistingIntervallDto>
          >(
            `${buildPath(
              `henvisninger/intervall/${intervalType.toLowerCase()}`
            )}?${params}`,
            signal
          );
        case OpplistingTyperEnum.Prøvesvar:
          return await httpGet<
            IntervallSearchDto<IfobtPrøvesvarOpplistingIntervallDto>
          >(
            `${buildPath(
              `ifobt/provesvar/intervall/${intervalType.toLowerCase()}`
            )}?${params}`,
            signal
          );
        case OpplistingTyperEnum.Koloskopirapport:
          return await httpGet<
            IntervallSearchDto<KoloskopirapportOpplistingIntervallDto>
          >(
            `${buildPath(
              `koloskopirapporter/intervall/${intervalType.toLowerCase()}`
            )}?${params}`,
            signal
          );
        case OpplistingTyperEnum.Henvendelse:
          return await httpGet<
            IntervallSearchDto<HenvendelseOpplistingIntervallDto>
          >(
            `${buildPath(
              `henvendelser/intervall/${intervalType.toLowerCase()}`
            )}?${params}`,
            signal
          );
        case OpplistingTyperEnum.Henvisningssvar:
          return await httpGet<
            IntervallSearchDto<HenvisningssvarOpplistingIntervallDto>
          >(
            `${buildPath(
              `henvisninger/svar/intervall/${intervalType.toLowerCase()}`
            )}?${params}`,
            signal
          );
        default:
          return new Promise((resolve) =>
            resolve({
              periodeTotal: {
                dateFrom: '',
                dateTo: '',
                ifobt: 0,
                koloskopi: 0,
                total: 0,
              },
              enheter: [],
            })
          );
      }
    } else {
      return new Promise((resolve) =>
        resolve({
          periodeTotal: {
            dateFrom: '',
            dateTo: '',
            ifobt: 0,
            koloskopi: 0,
            total: 0,
          },
          enheter: [],
        })
      );
    }
  } catch (error) {
    throw addMessageToExsistingFetchError(
      error as FetchResponseError,
      `Det skjedde en feil ved søk på intervall: ${
        (error as FetchResponseError).message
      }`
    );
  }
};

const getUtsendingPerIntervall: GetUtsendingPerIntervall = async (
  params,
  signal?
) => {
  const urlSearchParams = new URLSearchParams(params);
  const { intervalType } = Object.fromEntries(urlSearchParams.entries());
  try {
    return await httpGet<DeltakerutsendingerResponseDto>(
      `${buildPath(
        `deltakerutsendinger/typer/intervall/${intervalType.toLowerCase()}`
      )}?${params}`,
      signal
    );
  } catch (error) {
    throw addMessageToExsistingFetchError(
      error as FetchResponseError,
      `Det skjedde en feil ved søk på intervall: ${
        (error as FetchResponseError).message
      }`
    );
  }
};

const getUtsendingPerDeltaker: GetUtsendingPerDeltaker = async (
  params,
  signal?
) => {
  try {
    return await httpGet<DeltakerutsendingerDeltakerResponseDto>(
      `${buildPath(`deltakerutsendinger/intervall`)}?${params}`,
      signal
    );
  } catch (error) {
    throw addMessageToExsistingFetchError(
      error as FetchResponseError,
      `Det skjedde en feil ved søk på intervall: ${
        (error as FetchResponseError).message
      }`
    );
  }
};

const service: SentraladminService = {
  getDeltaker,
  getSenter,
  getUtsendingPerIntervall,
  getUtsendingPerDeltaker,
};

export default service;
